import { atom } from 'jotai'
import {
  ArrCalculation,
  Phase,
  PhaseDiscount,
  PhaseMinimum,
  Price,
  pricingDetailsAtom
} from './reviewPricing.primitives.atoms'
import {
  enhancePricesWithDiscounts,
  getDiscountScopeAvailability,
  getGlobalDiscount,
  getPricesForPhase,
  groupPricesByMinimum,
  PriceWithDiscount
} from '../utils/phases'
import { Currency } from '@sequencehq/api'
import {
  PhaseDuration,
  getRecurrenceDateForPhases,
  calculateAbsoluteDatesForPhases
} from '@sequencehq/utils'
import { duplicatePhase } from '../utils/duplicatePhase'
import { generateDefaultPhase } from '../utils/generateDefaultPhase'
import {
  reviewPricingFeaturesAtom,
  type ReviewPricingFeatures
} from './reviewPricing.features.atoms'

/**
 * Derived atoms to access specific fields in the pricing details.
 */
export const scheduleStartDateAtom = atom(
  get => {
    /**
     * We treat the start date of the first phase as the source of truth
     * for the schedule start date. This is more resilient to changes
     * in phases as they're deleted. We can be fancier later and maybe
     * change durations of phases and such and retain the start date, but
     * for now we'll just treat deleting the first phase as shifting the
     * start date to the next phase.
     */
    const { startDate } = get(pricingDetailsAtom)
    return startDate
  },
  (_get, set, newDate: Date) => {
    set(pricingDetailsAtom, {
      startDate: newDate
    })
  }
)

export type PhaseWithMinimumPriceGroups = {
  id: string
  name?: string
  startDate: Date
  endDate?: Date
  duration: PhaseDuration
  boundingBoxIndex: number
  minimumGroups: Array<{
    id: string
    boundingBoxIndex: number
    minimum?: PhaseMinimum & {
      billingFrequency: PriceWithDiscount['billingFrequency']
    }
    prices: Array<PriceWithDiscount>
  }>
  globalDiscount?: PhaseDiscount
  allPrices: Array<PriceWithDiscount>
  phasePriceMetadata: Phase['phasePriceMetadata']
  recurrencePreference: Phase['recurrencePreference']
  recurrenceDate: number
  availableFeatures: {
    addDiscount: boolean
    addMinimum: boolean
  }
}

export const phasesAtom = atom<Array<PhaseWithMinimumPriceGroups>>(get => {
  const { prices, phases, recurrenceDayOfMonth } = get(pricingDetailsAtom)
  const features = get(reviewPricingFeaturesAtom)
  const startDate = get(scheduleStartDateAtom)
  const phasesWithAbsoluteDates = calculateAbsoluteDatesForPhases({
    phases,
    startDate
  })
  const recurrenceDatesForPhases = getRecurrenceDateForPhases({
    phases: phasesWithAbsoluteDates,
    recurrenceDayOfMonth
  })

  return phasesWithAbsoluteDates.map((phase, idx) => {
    const phasePricesWithDiscounts = enhancePricesWithDiscounts({
      phase,
      prices
    })

    return {
      id: phase.id,
      name: phase.name,
      startDate: phase.startDate,
      endDate: phase.endDate,
      boundingBoxIndex: idx,
      /**
       * Even though we only allow one minimum per phase right now,
       * the plan is for more than one in the short-medium term when
       * we find some BE time to do so. Therefore, rather than need
       * a refactor, start out with the concept of n minimum groups,
       * and just treat 'no minimum' as one such group! It's a more
       * simple modelling anyhoo!
       */
      minimumGroups: groupPricesByMinimum({
        phase,
        prices: phasePricesWithDiscounts
      }).filter(({ prices }) => prices.length > 0),
      globalDiscount: getGlobalDiscount({
        phase
      }),
      phasePriceMetadata: phase.phasePriceMetadata,
      allPrices: phasePricesWithDiscounts,
      duration: phase.duration,
      recurrencePreference: phase.recurrencePreference,
      recurrenceDate:
        recurrenceDatesForPhases[idx]?.recurrenceDayOfMonth ??
        recurrenceDayOfMonth,
      availableFeatures: {
        addDiscount: canAddDiscountToPhase(phase, prices, features),
        addMinimum: canAddMinimumToPhase(phase, prices)
      }
    }
  })
})

const canAddDiscountToPhase = (
  phase: Phase,
  prices: Price[],
  features: ReviewPricingFeatures
): boolean => {
  if (!features.stackedDiscounts) {
    return !phase.discounts.find(
      discount => discount.restrictToPrices.length === 0
    )
  }

  const { availablePriceIds, isApplyToAllAvailable } =
    getDiscountScopeAvailability({ phase, prices })

  return isApplyToAllAvailable || availablePriceIds.length > 0
}

const canAddMinimumToPhase = (phase: Phase, prices: Price[]): boolean => {
  if (phase.minimums.length) return false

  const hasUsageBasedPrice = getPricesForPhase({ phase, prices }).some(
    price => 'usageMetricId' in price.structure
  )

  return hasUsageBasedPrice
}

export const scheduleCurrencyAtom = atom<Currency>(get => {
  const phases = get(phasesAtom)
  const firstPhase = phases[0]

  /**
   * Schedules do not have a defined currency. Instead they are derived
   * from the currency of the prices. Therefore, we take the currency
   * of the first price in the first phase.
   *
   * There isn't really a 'correct' fallback here, really, but for type
   * sanity we'll default to GBP so we don't need to account for
   * undefined throughout.
   */
  return firstPhase?.minimumGroups[0]?.prices[0]?.currency ?? 'GBP'
})

export const updatePhaseAtom = atom(
  null,
  (get, set, { id, update }: { id: string; update: Partial<Phase> }) => {
    const { phases } = get(pricingDetailsAtom)
    const updatedPhases = phases.map(phase => {
      if (phase.id !== id) {
        return phase
      }

      return {
        ...phase,
        ...update
      }
    })

    set(pricingDetailsAtom, {
      phases: updatedPhases
    })
  }
)

const deletePriceInPhaseDiscounts = ({
  phaseDiscounts,
  priceId
}: {
  phaseDiscounts: PhaseDiscount[]
  priceId: string
}) => {
  return phaseDiscounts
    .map(discount => {
      const remainingPrices = discount.restrictToPrices.filter(
        id => id !== priceId
      )

      return {
        ...discount,
        restrictToPrices:
          discount.restrictToPrices.length === 0 || remainingPrices.length > 0
            ? remainingPrices
            : null
      }
    })
    .filter(
      (discount): discount is PhaseDiscount =>
        discount.restrictToPrices !== null
    )
}

const deletePriceInPhaseMinimums = ({
  phaseMinimums,
  priceId
}: {
  phaseMinimums: PhaseMinimum[]
  priceId: string
}) => {
  return phaseMinimums
    .map(minimum => {
      return {
        ...minimum,
        restrictToPrices: minimum.restrictToPrices.filter(id => id !== priceId)
      }
    })
    .filter(minimum => minimum.restrictToPrices.length > 0)
}

/**
 * Clean out the price from the phase
 */
export const deletePriceInPhaseAtom = atom(
  null,
  (
    get,
    set,
    {
      phaseId,
      priceId
    }: {
      phaseId: string
      priceId: string
    }
  ) => {
    const { phases } = get(pricingDetailsAtom)
    const phase = phases.find(phase => phase.id === phaseId)

    if (!phase) {
      return
    }

    const remainingPriceIds = phase.priceIds.filter(id => id !== priceId)

    set(updatePhaseAtom, {
      id: phaseId,
      update: {
        priceIds: remainingPriceIds,
        minimums: deletePriceInPhaseMinimums({
          phaseMinimums: phase.minimums,
          priceId
        }),
        discounts: deletePriceInPhaseDiscounts({
          phaseDiscounts: phase.discounts,
          priceId
        })
      }
    })
  }
)

export const deletePhaseAtom = atom(null, (get, set, phaseId: string) => {
  const { phases } = get(pricingDetailsAtom)

  const [firstUpdatedPhase, ...updatedPhases] = phases.filter(
    phase => phase.id !== phaseId
  )

  /**
   * Always ensure the first phase is set to 'RESET' - in theory either should
   * be fine for the first phase on the BE (it is for any FE logic) but better
   * to be safe.
   */
  set(pricingDetailsAtom, {
    phases: [
      {
        ...firstUpdatedPhase,
        recurrencePreference: 'RESET'
      },
      ...updatedPhases
    ]
  })
})

export const deleteMinimumAtom = atom(
  null,
  (
    get,
    set,
    { phaseId, minimumId }: { phaseId: string; minimumId: string }
  ) => {
    const { phases } = get(pricingDetailsAtom)
    const phase = phases.find(phase => phase.id === phaseId)

    if (!phase) {
      return
    }

    set(updatePhaseAtom, {
      id: phaseId,
      update: {
        minimums: phase.minimums.filter(minimum => minimum.id !== minimumId)
      }
    })
  }
)

export const deleteDiscountAtom = atom(
  null,
  (
    get,
    set,
    {
      phaseId,
      discountId,
      priceId
    }: { phaseId: string; discountId: string; priceId?: string }
  ) => {
    const { phases } = get(pricingDetailsAtom)
    const phase = phases.find(phase => phase.id === phaseId)

    if (!phase) return

    set(updatePhaseAtom, {
      id: phaseId,
      update: {
        discounts: priceId
          ? deletePriceInPhaseDiscounts({
              phaseDiscounts: phase.discounts,
              priceId
            })
          : phase.discounts.filter(discount => discount.id !== discountId)
      }
    })
  }
)

export const productsAlreadyIncludedInPhases = atom<
  Record<Phase['id'], string[]>
>(get => {
  const phases = get(phasesAtom)
  return Object.entries(phases).reduce(
    (acc, [_, phase]) => ({
      ...acc,
      [phase.id]: phase.allPrices.map(price => price.productId)
    }),
    {}
  )
})

export const addPhaseAtom = atom(null, (get, set) => {
  const { phases } = get(pricingDetailsAtom)
  const currentLastPhase = phases.at(-1)

  if (!currentLastPhase) {
    set(pricingDetailsAtom, { phases: [generateDefaultPhase()] })
    return
  }

  const newPhase = duplicatePhase({ phase: currentLastPhase })

  set(pricingDetailsAtom, {
    phases: [...phases, newPhase]
  })
})

export const updatePriceArrCalculation = atom(
  null,
  (
    get,
    set,
    params: {
      priceId: string
      phaseId: string
      newValue: ArrCalculation
    }
  ) => {
    const { phases } = get(pricingDetailsAtom)
    const phase = phases.find(phase => phase.id === params.phaseId)

    if (!phase) {
      return
    }

    set(updatePhaseAtom, {
      id: params.phaseId,
      update: {
        phasePriceMetadata: [
          ...phase.phasePriceMetadata.filter(
            metadata => metadata.priceId !== params.priceId
          ),
          { priceId: params.priceId, arrCalculation: params.newValue }
        ]
      }
    })
  }
)
